<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { ArrowLeft, Headphones, Music2, Share2 } from "@lucide/vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { Track } from "@/types";

const route = useRoute();
const track = ref<Track | null>(null);
const recorded = ref(false);
const error = ref("");
const duration = computed(() => track.value ? `${Math.floor(track.value.durationMs / 60000)}:${String(Math.floor((track.value.durationMs % 60000) / 1000)).padStart(2, "0")}` : "—");

onMounted(async () => {
  try {
    track.value = (await api.publicTrack(String(route.params.id))).track;
  } catch {
    error.value = "暂时无法加载这首作品。";
  }
});

async function recordPlay() {
  if (!track.value || recorded.value) return;
  recorded.value = true;
  try {
    const result = await api.recordPlay(track.value.id);
    track.value.plays = result.plays;
  } catch {
    // 播放不应因统计失败而中断。
  }
}

async function share() {
  const data = { title: track.value?.title || "声浪工坊作品", url: location.href };
  if (navigator.share) await navigator.share(data);
  else {
    await navigator.clipboard.writeText(location.href);
    alert("链接已复制");
  }
}
</script>

<template>
  <main class="public-listen">
    <header class="listen-header">
      <router-link to="/"><span class="brand-mark"><Music2 :size="19" /></span><strong>声浪工坊</strong></router-link>
      <router-link class="text-link" to="/"><ArrowLeft :size="15" />返回工作室</router-link>
    </header>

    <section v-if="track" class="listen-stage">
      <div class="listen-art">
        <TrackArtwork :title="track.title" />
        <span class="orbit orbit-a" /><span class="orbit orbit-b" />
      </div>
      <div class="listen-copy">
        <p class="eyebrow">NEW INDEPENDENT RELEASE</p>
        <h1>{{ track.title }}</h1>
        <p class="listen-byline">{{ track.artist }} · {{ track.genre }} · {{ duration }}</p>
        <audio :src="track.audioUrl" controls preload="metadata" @play.once="recordPlay" />
        <div class="listen-actions">
          <button class="button button-lime" @click="share"><Share2 :size="17" />分享这首歌</button>
          <span><Headphones :size="16" />{{ track.plays }} 次公开试听</span>
        </div>
        <div class="listen-story">
          <span>ABOUT THIS TRACK</span>
          <p>{{ track.prompt }}</p>
        </div>
      </div>
    </section>
    <section v-else class="listen-missing">
      <span><Music2 :size="32" /></span><h1>{{ error || "正在加载作品…" }}</h1><router-link class="button button-lime" to="/">返回声浪工坊</router-link>
    </section>
    <footer class="listen-footer">由声浪工坊 × MiniMax Music 3.0 创作 · AI 辅助音乐作品</footer>
  </main>
</template>
